import React from "react";

import { useIntl } from "react-intl";
import { useSelector } from "react-redux";

import * as Dropdown from "ds/Dropdown";
import { Tooltip } from "ds/Tooltip";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import useUrls from "Hooks/useUrls";
import { meSelector } from "Reducers/app/selectors";
import { environmentSelector } from "Reducers/environment";
import {
  sortCLICommands,
  sortSSHCommands
} from "src/organization/common/containers/InfoDropButtons/utils";

import CommandsSection from "./CommandsSection";
import * as S from "./styles";

import type { Project } from "@packages/client";

type SSHDropdownButtonProps = {
  project: Project | undefined;
  appName: string;
  isWorker?: boolean;
};

const normalizeCurrentApp = currentAppWithSsh => {
  if (
    currentAppWithSsh?.appName &&
    currentAppWithSsh?.withWorkers &&
    currentAppWithSsh?.withoutWorkers
  ) {
    return {
      appName: currentAppWithSsh.appName,
      withWorkers: currentAppWithSsh.withWorkers,
      withoutWorkers: currentAppWithSsh.withoutWorkers
    };
  }
  return null;
};

const SSHDropdownButton = ({
  project,
  appName,
  isWorker = false
}: SSHDropdownButtonProps) => {
  const intl = useIntl();
  const environment = useSelectorWithUrlParams(environmentSelector);
  const username = useSelector(meSelector)?.username;
  const sshUrls = environment?.getSshUrls?.() || {};
  const [userSshUrl] = useUrls(["users.profile.sshKeys"], {
    username: username || ""
  });

  const sortedSSHCommands = sortSSHCommands(sshUrls);
  const sortedCLICommands = sortCLICommands({
    sshUrls,
    projectId: project?.id,
    environmentName: environment?.name,
    intl
  });

  const currentAppWithCli = sortedCLICommands.find(
    el => el.appName === appName
  );
  const currentAppWithSsh = sortedSSHCommands.find(
    el => el.appName === appName
  );

  const validCurrentAppWithCli = normalizeCurrentApp(currentAppWithCli);
  const validCurrentAppWithSsh = normalizeCurrentApp(currentAppWithSsh);

  const sshCommands = sshUrls
    ? Object.keys(sshUrls).map(key => sshUrls[key].replace("ssh://", "ssh "))
    : [];

  const canSsh = environment?._links?.ssh?.href;

  return (
    <S.Layout aria-label="Environment buttons">
      <S.Wrapper className="info-buttons">
        {environment &&
          sshCommands.length > 0 &&
          (canSsh ? (
            <Dropdown.CommandMenu
              triggerLabel={intl.formatMessage({ id: "ssh" })?.toUpperCase()}
              headerContent={
                <div>
                  <S.LinkSSH href={userSshUrl} analyticId="add_ssh_key">
                    {intl.formatMessage({ id: "add_ssh_key" })}
                  </S.LinkSSH>
                  <S.LinkDoc
                    href={intl.formatMessage({
                      id: "links.documentation.add_ssh_keys"
                    })}
                    analyticId="links.documentation.add_ssh_key"
                  >
                    {intl.formatMessage({ id: "ssh_documentation" })}
                  </S.LinkDoc>
                </div>
              }
              tabs={[
                {
                  id: "cli-section-cli",
                  label: intl.formatMessage({ id: "cli" }),
                  component: (
                    <S.ContentWrapper>
                      {validCurrentAppWithCli !== null && (
                        <CommandsSection
                          currentApp={validCurrentAppWithCli}
                          command="cliCommand"
                          isWorker={isWorker}
                        />
                      )}
                    </S.ContentWrapper>
                  )
                },
                {
                  id: "cli-section-ssh",
                  label: intl.formatMessage({ id: "ssh" }),
                  component: (
                    <S.ContentWrapper>
                      {validCurrentAppWithSsh !== null && (
                        <CommandsSection
                          currentApp={validCurrentAppWithSsh}
                          command="sshCommand"
                          isWorker={isWorker}
                        />
                      )}
                    </S.ContentWrapper>
                  )
                }
              ]}
            />
          ) : (
            <Tooltip tooltip={intl.formatMessage({ id: "ssh.disabled" })}>
              <Dropdown.CommandMenu
                triggerLabel={intl.formatMessage({ id: "ssh" })?.toUpperCase()}
                tabs={[]}
                disabled
              />
            </Tooltip>
          ))}
      </S.Wrapper>
    </S.Layout>
  );
};

export default SSHDropdownButton;
